import { APPSTATE, LAYOUTSTATE, MODALSTACK, MODALSTATE } from '..'
import { FloaterLocation, FloaterModal, ModalState } from '../../../types/state'
import APP_BREAKPOINTS from '../../components/styles/breakpoints'
import {
    COMPONENT_HEIGHTS,
    COMPONENT_WIDTHS,
    getBrowserBottomInset,
} from '../../components/theme'
import { FloaterSize, ModalEvent, ModalKind } from '../../constants/enums'
import {
    FLOATING_DEBUG,
    FLOATING_PREVIEW,
    MINI_PLAYER,
    MY_WEBCAM,
    QUICK_REPLY,
} from '../../constants/floaters'
import { isTouchDevice } from '../../utilities/browser'
import { safeObj } from '../../utilities/safe'
import { dockX } from '../../utilities/shrinkToEdge'
import { customStateWrapper } from '../customStateWrapper'
import { getModalState, getScreenState } from '../getState'
import { pubSubModal, pubSubModalStack } from '../pubSub'

export const QUICK_REPLY_WIDTH = 320
export const QUICK_REPLY_MOBILE_WIDTH = 260
export const QUICK_REPLY_CHAT_HEIGHT = 260
export const QUICK_REPLY_MOBILE_CHAT_HEIGHT = 190
export const QUICK_REPLY_HEIGHT =
    COMPONENT_HEIGHTS.chatHeader +
    COMPONENT_HEIGHTS.compose +
    QUICK_REPLY_CHAT_HEIGHT
export const QUICK_REPLY_MOBILE_HEIGHT =
    COMPONENT_HEIGHTS.chatHeader +
    COMPONENT_HEIGHTS.compose +
    QUICK_REPLY_MOBILE_CHAT_HEIGHT
const MY_WEBCAM_VIDEO_WIDTH = 320
const MY_WEBCAM_VIDEO_HEIGHT = 180
const MY_WEBCAM_TOP_SECTION_HEIGHT = 90
const MY_WEBCAM_BOTTOM_SECTION_HEIGHT = 50
const MY_WEBCAM_HEIGHT_LARGE =
    MY_WEBCAM_TOP_SECTION_HEIGHT +
    MY_WEBCAM_VIDEO_HEIGHT +
    MY_WEBCAM_BOTTOM_SECTION_HEIGHT +
    16
const MY_WEBCAM_HEIGHT_MEDIUM =
    MY_WEBCAM_VIDEO_HEIGHT + MY_WEBCAM_BOTTOM_SECTION_HEIGHT + 16
const MY_WEBCAM_HEIGHT_SMALL = MY_WEBCAM_VIDEO_HEIGHT

const MY_WEBCAM_WIDTHS = {
    [FloaterSize.Small]: MY_WEBCAM_VIDEO_WIDTH,
    [FloaterSize.Medium]: MY_WEBCAM_VIDEO_WIDTH + 16,
    [FloaterSize.Large]: MY_WEBCAM_VIDEO_WIDTH + 16,
}
const MY_WEBCAM_HEIGHTS = {
    [FloaterSize.Small]: MY_WEBCAM_HEIGHT_SMALL,
    [FloaterSize.Medium]: MY_WEBCAM_HEIGHT_MEDIUM,
    [FloaterSize.Large]: MY_WEBCAM_HEIGHT_LARGE,
}

type EmitModalUpdateState = {
    modalKind: ModalKind
    modal: ModalState[ModalKind]
}
type EmitModalUpdateOptions = {
    deleteRoom?: boolean
    immediate?: boolean
    skipEmit?: boolean
}

export const emitModalUpdate = customStateWrapper<
    EmitModalUpdateState,
    EmitModalUpdateOptions
>(({ modalKind }, { skipEmit = false, immediate = false } = {}) => {
    if (!skipEmit)
        pubSubModal.emit(
            modalEventMap[modalKind],
            MODALSTATE[modalKind],
            immediate,
        )
})(({ modalKind, modal }) => {
    MODALSTATE[modalKind] = modal as any
})

type SetModalStackState = { modalKey: string }
type SetModalStackOptions = {
    pushDown?: boolean
    immediate?: boolean
    deleteKey?: boolean
}

export const setModalStack = customStateWrapper<
    SetModalStackState,
    SetModalStackOptions
>((_, { immediate = false }) => {
    pubSubModalStack.emit(ModalEvent.UPDATE_MODAL_STACK, MODALSTACK, immediate)
})(({ modalKey }, { pushDown = false, deleteKey = false }) => {
    const currentPosition = MODALSTACK.byKey[modalKey]
    // find and remove the current position of the modalKey
    if (currentPosition !== undefined && currentPosition >= 0) {
        MODALSTACK.byPosition.splice(currentPosition, 1)
    }
    // if we're not just deleting, then add the modalKey where we need it
    if (!deleteKey) {
        if (pushDown) MODALSTACK.byPosition.push(modalKey)
        else MODALSTACK.byPosition = [modalKey].concat(MODALSTACK.byPosition)
    }
    // recreate the byKey object
    MODALSTACK.byKey = {}
    MODALSTACK.byPosition.forEach(
        (key, position) => (MODALSTACK.byKey[key] = position),
    )
})

const modalEventMap = {
    [ModalKind.Dialog]: ModalEvent.UPDATE_DIALOG,
    [ModalKind.Drawer]: ModalEvent.UPDATE_DRAWER,
    [ModalKind.Confirmation]: ModalEvent.UPDATE_CONFIRMATION,
    [ModalKind.Floater]: ModalEvent.UPDATE_FLOATER,
    [ModalKind.FloatingRoom]: ModalEvent.UPDATE_FLOATING_ROOM,
    [ModalKind.QuickMenu]: ModalEvent.UPDATE_QUICK_MENU,
}

export const generateModalKey = (type: string, id?: number) => {
    switch (type) {
        case MY_WEBCAM:
        case FLOATING_DEBUG:
            return type
        default:
            return `${id}-${type}`
    }
}

export const setMultiModal = (
    key: string,
    modal?: FloaterModal,
    { immediate = false, skipEmit = false, skipStack = false } = {},
) => {
    const FLOATERS = getModalState()[ModalKind.Floater]

    if (!skipStack) setModalStack({ modalKey: key }, { deleteKey: !modal.show })
    emitModalUpdate(
        {
            modalKind: ModalKind.Floater,
            modal: {
                ...FLOATERS,
                [key]: modal,
            },
        },
        { immediate, skipEmit },
    )
}

export const setModal = <
    Kind extends
        | ModalKind.Confirmation
        | ModalKind.Dialog
        | ModalKind.Drawer
        | ModalKind.FloatingRoom
        | ModalKind.QuickMenu,
>(
    modalKind: Kind,
    // modalType: string,
    modal: ModalState[Kind],
    // showModal = false,
    { immediate = false, skipEmit = false, skipStack = false } = {},
) => {
    if (!skipStack)
        setModalStack({ modalKey: modalKind }, { deleteKey: !modal.show })
    // const modal = {
    //     state: modalState,
    //     type: modalType,
    //     show: showModal,
    // } as ModalState[Kind]
    emitModalUpdate({ modalKind, modal }, { immediate, skipEmit })
}

export const setShowModal = (
    modalKind:
        | ModalKind.Confirmation
        | ModalKind.Dialog
        | ModalKind.Drawer
        | ModalKind.QuickMenu,
    showModal: boolean,
    immediate = false,
    skipEmit = false,
) => {
    const existingModal = getModalState()[modalKind]
    setModalStack({ modalKey: modalKind }, { deleteKey: !showModal })
    const modal = { ...existingModal, show: showModal }
    emitModalUpdate({ modalKind, modal }, { immediate, skipEmit })
}
export const setShowMultiModal = (
    key: string,
    showModal: boolean,
    immediate = false,
) => {
    setModalStack({ modalKey: key }, { deleteKey: !showModal })
    const existing = getModalState().floater[key]
    // only set show status if we have a floater that exists
    if (existing) {
        const modal = {
            ...getModalState().floater,
            [key]: {
                ...existing,
                show: showModal,
            },
        }
        emitModalUpdate({ modalKind: ModalKind.Floater, modal }, { immediate })
    }
}

export const PREVIEW_SIZES = {
    [FloaterSize.Small]: () =>
        Math.min(Math.max(getScreenState().windowWidth * 0.4, 180), 225),
    [FloaterSize.Medium]: () =>
        (Math.max((getScreenState().windowWidth || 800) / 2 - 50 * 2, 300) -
            200) /
            2 +
        200,
    [FloaterSize.Large]: () =>
        Math.max((getScreenState().windowWidth || 800) / 2 - 50 * 2, 300),
    [FloaterSize.Fullscreen]: () => {
        return getScreenState().contentWidth
    },
}

/** Remove a keyed floater outright (show: false would keep its entry) */
export const removeMultiModal = (key: string) => {
    const { [key]: _, ...floaters } = getModalState().floater
    emitModalUpdate(
        { modalKind: ModalKind.Floater, modal: floaters },
        { immediate: true },
    )
    setModalStack({ modalKey: key }, { deleteKey: true })
}

export const closeFloatingPreview = (id: number) =>
    removeMultiModal(generateModalKey(FLOATING_PREVIEW, id))

export const getNewExpandLocation = (
    modalKey: string,
    type: string,
    proposedSize?: FloaterSize,
    proposedLocation?: FloaterLocation,
): [newLocation: FloaterLocation, changed: boolean] => {
    const floaterState = safeObj(getModalState().floater[modalKey])
    const {
        locations: foundLocations = {},
        // close = true,
        size: existingSize,
    } = floaterState
    const size = proposedSize ?? existingSize
    const [orientation, portrait] = getFloaterOrientation()
    const currentLocation = proposedLocation
        ? { ...proposedLocation }
        : foundLocations[orientation]
    const { windowHeight, windowWidth } = getScreenState()
    let changed = false
    // check to make sure the floater isnt off screen somehow
    // if screen size is <600 px then center it upon de-minimize
    if (type === QUICK_REPLY) {
        // off the bottom
        if (currentLocation.y + QUICK_REPLY_HEIGHT > windowHeight) {
            currentLocation.y = windowHeight - (QUICK_REPLY_HEIGHT + 24)
            changed = true
        }
        // too close to the top
        if (currentLocation.y < 8) {
            currentLocation.y = 8
            changed = true
        }
        // too close to the left side
        if (currentLocation.x < 8) {
            currentLocation.x = 8
            changed = true
        }
        if (currentLocation.x + QUICK_REPLY_WIDTH + 8 >= windowWidth) {
            currentLocation.x = windowWidth - (QUICK_REPLY_WIDTH + 8)
            changed = true
        }
        // just ensure if the current x + QUICK_REPLY_WIDTH > W
        return [{ ...currentLocation }, changed]
    } else if (type === MY_WEBCAM) {
        const bottomOffset = portrait ? COMPONENT_HEIGHTS.navBottom + 8 : 24
        // off the bottom
        if (
            currentLocation.y + MY_WEBCAM_HEIGHTS[size] + bottomOffset >
            windowHeight
        ) {
            currentLocation.y =
                windowHeight - (MY_WEBCAM_HEIGHTS[size] + bottomOffset)
            changed = true
        }
        // too close to the top
        if (currentLocation.y < 8) {
            currentLocation.y = 8
            changed = true
        }
        // too close to the left side
        if (currentLocation.x < 8) {
            currentLocation.x = 8
            changed = true
        }
        if (currentLocation.x + MY_WEBCAM_WIDTHS[size] + 36 > windowWidth) {
            currentLocation.x = windowWidth - (MY_WEBCAM_WIDTHS[size] + 36)
            changed = true
        }
        return [{ ...currentLocation }, changed]
    } else if (type === FLOATING_PREVIEW || type === MINI_PLAYER) {
        // check the current size to find the dimension boundaries to check
        const compactHeader =
            windowWidth < APP_BREAKPOINTS.md || APPSTATE.app.isMobile
        const { expandedSearchMobile } = LAYOUTSTATE.layout
        const headerHeight =
            (compactHeader
                ? COMPONENT_HEIGHTS.headerCompact
                : COMPONENT_HEIGHTS.headerDesktop) +
            (expandedSearchMobile ? COMPONENT_HEIGHTS.inputSearch + 8 : 0)

        const calcWidth = PREVIEW_SIZES[size]()
        const videoHeight = (calcWidth / 16) * 9
        const newPreviewHeight =
            videoHeight +
            (size >= FloaterSize.Medium ? COMPONENT_HEIGHTS.chatHeader : 0)

        const usableWindowHeight = portrait
            ? windowHeight -
              (headerHeight +
                  COMPONENT_HEIGHTS.navBottom +
                  getBrowserBottomInset())
            : windowHeight - headerHeight
        const usableWindowWidth = portrait
            ? windowWidth
            : windowWidth - COMPONENT_WIDTHS.leftNav

        // off the bottom
        if (typeof currentLocation.y === 'string') {
            const percentage = parseInt(currentLocation.y.split('%')[0], 10)
            if (percentage) {
                const calcY = (percentage / 100) * windowHeight

                if (calcY + newPreviewHeight > usableWindowHeight) {
                    const adjustedY =
                        usableWindowHeight - (newPreviewHeight + 8)
                    const newYPercentage = Math.floor(
                        (adjustedY / windowHeight) * 100,
                    )
                    currentLocation.y = `${newYPercentage}%`
                    changed = true
                }
            }
        }
        if (currentLocation.y + newPreviewHeight > usableWindowHeight) {
            currentLocation.y = usableWindowHeight - (newPreviewHeight + 8)
            changed = true
        }
        // too close to the top
        if (currentLocation.y < 8) {
            currentLocation.y = 8
            changed = true
        }
        const { close = true, shrinkToEdge = false } =
            floaterState as FloaterModal
        // a mini player docked at an edge stays on that edge
        if (type === MINI_PLAYER && shrinkToEdge && !proposedLocation) {
            const x = dockX(shrinkToEdge, calcWidth, windowWidth)
            if (x !== currentLocation.x) {
                currentLocation.x = x
                changed = true
            }
        } else {
            // too close to the left side
            if (portrait) {
                if (currentLocation.x < 8) {
                    currentLocation.x = 8
                    changed = true
                }
            } else {
                if (LAYOUTSTATE.layout.expandedNav) {
                    if (currentLocation.x < COMPONENT_WIDTHS.leftNavExpanded) {
                        currentLocation.x = COMPONENT_WIDTHS.leftNavExpanded
                        changed = true
                    }
                } else {
                    if (currentLocation.x < COMPONENT_WIDTHS.leftNav) {
                        currentLocation.x = COMPONENT_WIDTHS.leftNav
                        changed = true
                    }
                }
            }
            // too close to right side
            if (
                currentLocation.x + calcWidth + (close ? 36 : 8) >
                windowWidth
            ) {
                currentLocation.x =
                    usableWindowWidth - (calcWidth + (close ? 36 : 8))
                changed = true
            }
        }
        return [
            {
                ...currentLocation,
                width: calcWidth,
                height: newPreviewHeight,
            },
            changed,
        ]
    }
    return [currentLocation, changed]
}

export const repositionFloater = (floater: FloaterModal) => {
    if (!floater.show) return
    // 2) check if they're offscreen
    const modalKey = generateModalKey(floater.type, floater.id)
    const [newLocation, changed] = getNewExpandLocation(modalKey, floater.type)
    if (!changed) return
    // 3) reposition as needed (only emit if we've updated location)
    const [orientation] = getFloaterOrientation()
    setMultiModal(modalKey, {
        ...floater,
        locations: {
            ...floater.locations,
            [orientation]: newLocation,
        },
    })
}
export const getFloaterOrientation = (): [
    orientation: 'portrait' | 'landscape',
    isPortrait: boolean,
] => {
    const { portrait } = getScreenState()
    const isMobile = APPSTATE.app.isMobile || isTouchDevice
    if (!isMobile) return ['landscape', false]
    return [portrait ? 'portrait' : 'landscape', portrait]
}
